import { createPluginViewStateAdapter } from './createPluginViewStateAdapter';
import deletePendingInlineAttachment from 'owa-calendar-compose-form-store/lib/utils/deletePendingInlineAttachment';
import getEventClientItemId from 'owa-calendar-compose-form-store/lib/utils/getEventClientItemId';
import {
    addPendingInlineImage,
    getPendingInlineImages,
} from 'owa-calendar-compose-form-store/lib/utils/pendingInlineImages';
import { isProofingSupported } from 'owa-editor-proofing-common';
import { FullComposeEditorPlugins, FullComposeViewState } from 'owa-calendar-compose-types';
import { getAllRecipientsFromAttendees } from 'owa-calendar-compose-form-store/lib/utils/getRecipientsFromAttendees';
import getCurrentEventOrganizer from 'owa-calendar-compose-form-store/lib/selectors/getCurrentEventOrganizer';
import {
    addAttendeeFromEmailAddress,
    onIsDraftChanged,
    saveEventDraft,
    addPersonaToRequiredAttendees,
    removePersonaFromRequiredAttendees,
} from 'owa-calendar-compose-form-actions';
import createAttachments from 'owa-calendar-compose-form-store/lib/actions/createAttachments';
import DockingPlugin from 'owa-editor-docking-plugin/lib/plugin/DockingPlugin';
import { LazyAtMentionsPickerPluginClass } from 'owa-editor-mentions-picker-plugin';
import { LazyEmojiPickerPluginClass } from 'owa-editor-emoji-picker-plugin';
import { LazyLinkPluginClass } from 'owa-editor-link-plugin';
import { LazyProofingPluginClass } from 'owa-editor-proofing-plugin';
import RibbonPlugin from 'owa-editor-ribbonplugin/lib/plugin/RibbonPlugin';
import { isFeatureEnabled } from 'owa-feature-flags';
import InlineImagePlugin from 'owa-inline-image/lib/plugins/InlineImagePlugin';
import { LazyInlineImageResizePluginClass } from 'owa-inline-image-resize-plugin';
import { LazyInlineImageCropPluginClass } from 'owa-inline-image-crop-plugin';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { getAttendeeLists } from 'owa-calendar-compose-form-store/lib/utils/getAttendees';
import { getComposeStore } from 'owa-calendar-compose-store-manager';
import { isFTDFSeriesMaster, isEventAMeeting } from 'owa-calendar-event-capabilities';
import { LazyFluidPluginClass } from 'owa-fluid-integrations';
import { isFluidEnabledForSource, FluidOwaSource } from 'owa-fluid-validations';
import SpellcheckPlugin from 'owa-editor-proofing-plugin/lib/plugin/SpellcheckPlugin';
import { LazyPasteOptionPluginClass } from 'owa-editor-paste-option-plugin';
import { LazyTableEditPluginClass } from 'owa-editor-table-edit-plugin';
import { CreateAttachmentsProperties } from 'owa-attachment-well-data';
import { LazyInlineImagePluginClass } from 'owa-editor-inlineimage-plugin';
import { AttachmentFileList } from 'owa-attachment-file-types';
import { LazyRotateImagePluginClass } from 'owa-editor-rotate-image-plugin';
import getBottomDockingHeight from './getBottomDockingHeight';

export default function createFullComposeEditorPlugins(
    viewState: FullComposeViewState
): FullComposeEditorPlugins {
    const { event, originalEvent } = getComposeStore();
    const canModifyRecipients = !(isFTDFSeriesMaster(event) && isEventAMeeting(originalEvent));
    const editorAdapter = createPluginViewStateAdapter(viewState, 'bodyViewState');
    const userConfiguration = getUserConfiguration();
    const isRtl = userConfiguration.SessionSettings.IsUserCultureRightToLeft;
    const isInlineImageV2Enabled = isFeatureEnabled('cmp-inlineImageV2');
    const attachmentWellViewStateAdapter = createPluginViewStateAdapter(
        viewState,
        'attachmentWell'
    );
    const attachmentHandler = createAttachmentHandler(viewState);

    // NOTE: If the plugin has a parameter that is a function on the view state,
    // use createPluginViewStateAdapter so the current view state is retrieved
    // at the time it is called (VSO 54506)
    return {
        docking: new DockingPlugin(
            createPluginViewStateAdapter(viewState, 'docking'),
            editorAdapter,
            getBottomDockingHeight
        ),
        ribbon: new RibbonPlugin(createPluginViewStateAdapter(viewState, 'ribbon'), editorAdapter),
        emojiPicker: new LazyEmojiPickerPluginClass(
            createPluginViewStateAdapter(viewState, 'emojiPicker'),
            editorAdapter,
            false /*useFlexPane*/,
            viewState.expressionId
        ),
        inlineImage:
            !isInlineImageV2Enabled &&
            new InlineImagePlugin(
                createPluginViewStateAdapter(viewState, 'inlineImage'),
                editorAdapter,
                attachmentWellViewStateAdapter,
                attachmentHandler,
                placeholderId =>
                    deletePendingInlineAttachment(placeholderId, viewState.attachmentWell),
                null /* isInlineImageDiabled */,
                event.ParentFolderId.mailboxInfo.userIdentity
            ),
        spellcheckPlugin: new SpellcheckPlugin(),
        inlineImageV2:
            isInlineImageV2Enabled &&
            new LazyInlineImagePluginClass(
                createPluginViewStateAdapter(viewState, 'inlineImageV2'),
                editorAdapter,
                attachmentWellViewStateAdapter,
                attachmentHandler,
                false /*isInlineIMageDisabled*/,
                event.ParentFolderId.mailboxInfo.userIdentity
            ),
        inlineImageResize: new LazyInlineImageResizePluginClass(),
        inlineImageCrop: isFeatureEnabled('cmp-imageCrop')
            ? new LazyInlineImageCropPluginClass()
            : null,
        link: new LazyLinkPluginClass(
            viewState.bodyViewState,
            viewState.attachmentWell,
            getAttendeeLists,
            getFromAddress(),
            true /* isCalendar */,
            false /* allowCtrlK */
        ),
        mentionsPicker: new LazyAtMentionsPickerPluginClass(
            createPluginViewStateAdapter(viewState, 'mentionsPicker'),
            editorAdapter,
            addPersonaToRequiredAttendees,
            removePersonaFromRequiredAttendees,
            null,
            canModifyRecipients,
            getAllRecipientsFromAttendees
        ),
        proofing: isProofingSupported()
            ? new LazyProofingPluginClass(
                  createPluginViewStateAdapter(viewState, 'proofing'),
                  editorAdapter,
                  createPluginViewStateAdapter(viewState, 'augloopSession'),
                  'Calendar',
                  true /* isCalendar */,
                  viewState
              )
            : null,
        // TODO VSO 100597: support at mentions in fluid in calendar
        fluid: isFluidEnabledForSource(FluidOwaSource.AgendaEditor)
            ? new LazyFluidPluginClass(
                  viewState.bodyViewState,
                  FluidOwaSource.AgendaEditor,
                  addAttendeeFromEmailAddress,
                  viewState.collabSpaceViewStateId
              )
            : null,
        pasteOption: new LazyPasteOptionPluginClass(
            createPluginViewStateAdapter(viewState, 'pasteOption'),
            editorAdapter,
            isRtl
        ),
        tableEdit: new LazyTableEditPluginClass(),
        rotateImage: isFeatureEnabled('cmp-rotateImage')
            ? new LazyRotateImagePluginClass(
                  createPluginViewStateAdapter(viewState, 'rotateImage'),
                  editorAdapter,
                  attachmentHandler
              )
            : null,
    };
}

function getFromAddress(): string {
    const organizer = getCurrentEventOrganizer();
    return organizer
        ? organizer.Mailbox.EmailAddress
        : getUserConfiguration().SessionSettings.UserEmailAddress;
}

function createAttachmentHandler(viewState: FullComposeViewState) {
    return (files: AttachmentFileList, properties: CreateAttachmentsProperties) => {
        let itemId = getEventClientItemId();
        if (itemId?.Id) {
            createAttachments(viewState, getEventClientItemId(), files, properties);
        } else {
            const hasPendingInlineImage = getPendingInlineImages().length > 0;

            for (let i = 0; i < files.length; i++) {
                addPendingInlineImage(files[i]);
            }

            if (!hasPendingInlineImage) {
                onIsDraftChanged(true /*isDraft*/);
                saveEventDraft();
            }
        }
    };
}
